import { SCHEDULE_API_BASE_URL } from '../environment';
import HttpRequest from '../request';
import { getAppointmentTagForCurrentSession, getAuth } from '../storage';
import { getAvailabilityParams } from './helpers';
import {
  AcuityAppointment,
  AcuityAppointmentTag,
  AcuityCancelAppointmentSource,
  AppointmentTypeWithAvailableSlots,
  DailyAvailability,
  DecryptedSchedulingLink,
  DecryptedSchedulingLinkResponse,
  GroupClassFilteringCategory,
  MonthlyAvailability,
  ScheduledMeeting,
  Specialty,
} from './types';

class Scheduling extends HttpRequest {
  public async getMonthlyAvailability({
    appointmentTypeId,
    calendarId,
    existingCoreAppointmentId,
    month,
    specialty,
  }: {
    appointmentTypeId: string | null;
    calendarId: string | null;
    existingCoreAppointmentId: string | null;
    month: string;
    specialty: Specialty | null;
  }): Promise<MonthlyAvailability | null> {
    const monthlyAvailabilityResponse = await this.get<{ success: boolean } & MonthlyAvailability>({
      url: `/v2/members/${getAuth()?.id}/appointments/follow-up/available-dates`,
      params: {
        ...(calendarId && { calendarId }),
        ...(existingCoreAppointmentId && { existingCoreAppointmentId }),
        ...getAvailabilityParams(appointmentTypeId, specialty),
        month,
      },
      options: { authAs: 'header' },
    });

    if (monthlyAvailabilityResponse) {
      const { success, ...rest } = monthlyAvailabilityResponse;

      return rest;
    }

    return null;
  }

  public async getDailyAvailability({
    appointmentTypeId,
    calendarId,
    date,
    existingCoreAppointmentId,
    specialty,
  }: {
    appointmentTypeId: string | null;
    calendarId: string | null;
    date: string;
    existingCoreAppointmentId: string | null;
    specialty: Specialty | null;
  }): Promise<DailyAvailability | null> {
    const dailyAvailabilityResponse = await this.get<{ success: boolean } & DailyAvailability>({
      url: `/v2/members/${getAuth()?.id}/appointments/follow-up/available-times`,
      params: {
        ...(calendarId && { calendarId }),
        ...(existingCoreAppointmentId && { existingCoreAppointmentId }),
        ...getAvailabilityParams(appointmentTypeId, specialty),
        date,
      },
      options: { authAs: 'header' },
    });

    if (dailyAvailabilityResponse) {
      const { success, ...rest } = dailyAvailabilityResponse;

      return rest;
    }

    return null;
  }

  public async createAppointment({
    appointmentTypeId,
    dateTime,
    calendarId,
  }: {
    appointmentTypeId: number;
    dateTime: string;
    calendarId: number;
  }): Promise<AcuityAppointment | null> {
    return await this.post<AcuityAppointment>({
      url: `schedule/v1/calendars/${calendarId}/appointments?shouldNotAssignProvider=true`,
      payload: { appointmentTypeId, datetime: dateTime, tag: getAppointmentTagForCurrentSession() },
      options: { authAs: 'header' },
    });
  }

  public async cancelAppointment({
    memberId,
    appointmentId,
  }: {
    memberId: number;
    appointmentId: number;
  }): Promise<number | null> {
    const cancelAppointmentResponse = await this.put<{ cancelledAppointment: { id: number } }>({
      url: `/v1/members/${memberId}/appointments/${appointmentId}/cancel`,
      payload: { source: AcuityCancelAppointmentSource.MemberWebAppManageAppointment },
      options: { authAs: 'header' },
    });

    const {
      cancelledAppointment: { id },
    } = cancelAppointmentResponse || { cancelledAppointment: { id: 0 } };

    return id ?? null;
  }

  public async rescheduleAppointment({
    dateTime,
    existingCoreAppointmentId,
  }: {
    dateTime: string;
    existingCoreAppointmentId: string;
  }): Promise<AcuityAppointment | null> {
    const rescheduledAppointmentResponse = await this.put<{
      rescheduledAppointment: AcuityAppointment;
    }>({
      options: { authAs: 'header' },
      payload: { dateTime, source: AcuityAppointmentTag.MemberAppReschedule },
      url: `/v4/members/${getAuth()?.uuid}/appointments/${existingCoreAppointmentId}/reschedule`,
    });

    return rescheduledAppointmentResponse?.rescheduledAppointment || null;
  }

  public async decryptSchedulingLinkToRawAcuityLink(): Promise<DecryptedSchedulingLink | null> {
    const hash = new URLSearchParams(window.location.search).get('p');

    if (!hash) {
      return null;
    }

    const response = await this.get<{ success: boolean } & DecryptedSchedulingLinkResponse>({
      url: `/v1/scheduling-links/${hash}`,
    });

    if (!response) {
      return null;
    }

    return response.linkInfo;
  }

  public async getGroupClassesWithAvailability(
    category: GroupClassFilteringCategory | 'all'
  ): Promise<AppointmentTypeWithAvailableSlots[] | null> {
    const groupClassesResponse = await this.get<{
      appointmentTypes: AppointmentTypeWithAvailableSlots[];
    }>({
      url: '/v3/members/me/appointments/group-classes/availability',
      ...(category !== 'all' && { params: { category } }),
      options: { authAs: 'header' },
    });

    return groupClassesResponse?.appointmentTypes || null;
  }

  public async getMeetingByCoreAppointmentId(
    coreAppointmentId?: number
  ): Promise<ScheduledMeeting | null> {
    return (
      (
        await this.get<{ meeting: ScheduledMeeting }>({
          url: `/v1/meetings/${coreAppointmentId}`,
          options: { authAs: 'header' },
        })
      )?.meeting || null
    );
  }
}

const schedulingClient = new Scheduling(SCHEDULE_API_BASE_URL);

export default schedulingClient;
